import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Ticket, Phone, Shield, Search, ArrowLeft, Loader2, CheckCircle2, AlertCircle, Monitor, Laptop, Smartphone, Tablet, HelpCircle, MessageSquarePlus } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const DEVICE_TYPES = [
  { value: 'Desktop', icon: Monitor, label: 'Desktop' },
  { value: 'Laptop', icon: Laptop, label: 'Laptop' },
  { value: 'Phone', icon: Smartphone, label: 'Phone' },
  { value: 'Tablet', icon: Tablet, label: 'Tablet' },
  { value: 'Other', icon: HelpCircle, label: 'Other' },
];

const SupportPage = () => {
  const [mode, setMode] = useState(null); // null | 'submit' | 'check' | 'update'
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Form fields
  const [customerName, setCustomerName] = useState('');
  const [deviceType, setDeviceType] = useState('');
  const [issueDescription, setIssueDescription] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [normalizedPhone, setNormalizedPhone] = useState('');

  // Results
  const [createdTicket, setCreatedTicket] = useState(null);
  const [lookupResult, setLookupResult] = useState(null);
  const [allTickets, setAllTickets] = useState([]);
  const [ticketIdLookup, setTicketIdLookup] = useState('');

  // Update ticket state
  const [updateTicketId, setUpdateTicketId] = useState('');
  const [updateMessage, setUpdateMessage] = useState('');
  const [updateSuccess, setUpdateSuccess] = useState(false);

  const resetAll = () => {
    setMode(null);
    setStep(1);
    setError('');
    setCustomerName('');
    setDeviceType('');
    setIssueDescription('');
    setPhoneNumber('');
    setOtpCode('');
    setNormalizedPhone('');
    setCreatedTicket(null);
    setLookupResult(null);
    setAllTickets([]);
    setTicketIdLookup('');
    setUpdateTicketId('');
    setUpdateMessage('');
    setUpdateSuccess(false);
    setLoading(false);
  };

  // ---------- Submit Flow ----------

  const handleSendOTP = async () => {
    if (!phoneNumber.trim()) { setError('Please enter your phone number'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/tickets/auth/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone_number: phoneNumber })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to send code');
      setNormalizedPhone(data.phone);
      setStep(mode === 'submit' ? 3 : 2);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyAndSubmit = async () => {
    if (!otpCode.trim()) { setError('Please enter the verification code'); return; }
    setLoading(true);
    setError('');
    try {
      // Verify OTP
      const verifyRes = await fetch(`${API}/api/tickets/auth/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone_number: normalizedPhone, code: otpCode })
      });
      const verifyData = await verifyRes.json();
      if (!verifyRes.ok) throw new Error(verifyData.detail || 'Invalid code');

      // Create ticket
      const ticketRes = await fetch(`${API}/api/tickets/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone_number: normalizedPhone,
          customer_name: customerName,
          device_type: deviceType,
          issue_description: issueDescription
        })
      });
      const ticketData = await ticketRes.json();
      if (!ticketRes.ok) throw new Error(ticketData.detail || 'Failed to create ticket');
      setCreatedTicket(ticketData);
      setStep(4);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ---------- Check Status Flow ----------

  const handleVerifyAndLookup = async () => {
    if (!otpCode.trim()) { setError('Please enter the verification code'); return; }
    setLoading(true);
    setError('');
    try {
      const verifyRes = await fetch(`${API}/api/tickets/auth/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone_number: normalizedPhone, code: otpCode })
      });
      const verifyData = await verifyRes.json();
      if (!verifyRes.ok) throw new Error(verifyData.detail || 'Invalid code');

      // Fetch all tickets for this phone
      const ticketsRes = await fetch(`${API}/api/tickets/by-phone/${encodeURIComponent(normalizedPhone)}`);
      const ticketsData = await ticketsRes.json();
      setAllTickets(ticketsData.tickets || []);
      setStep(3);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLookupTicket = async () => {
    if (!ticketIdLookup.trim()) { setError('Please enter a ticket number'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/tickets/lookup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone_number: normalizedPhone, ticket_id: ticketIdLookup })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Ticket not found');
      setLookupResult(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ---------- Update Ticket Flow ----------

  const handleVerifyAndFetchForUpdate = async () => {
    if (!otpCode.trim()) { setError('Please enter the verification code'); return; }
    setLoading(true);
    setError('');
    try {
      const verifyRes = await fetch(`${API}/api/tickets/auth/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone_number: normalizedPhone, code: otpCode })
      });
      const verifyData = await verifyRes.json();
      if (!verifyRes.ok) throw new Error(verifyData.detail || 'Invalid code');

      // Fetch all tickets for this phone
      const ticketsRes = await fetch(`${API}/api/tickets/by-phone/${encodeURIComponent(normalizedPhone)}`);
      const ticketsData = await ticketsRes.json();
      setAllTickets(ticketsData.tickets || []);
      setStep(3);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitUpdate = async () => {
    if (!updateMessage.trim()) { setError('Please enter your update message'); return; }
    if (!updateTicketId) { setError('Please select a ticket'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/tickets/customer-update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone_number: normalizedPhone,
          ticket_id: updateTicketId,
          update_message: updateMessage
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to update ticket');
      setUpdateSuccess(true);
      setStep(4);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const statusColor = (status) => {
    switch (status) {
      case 'open': return 'text-cyan-400 bg-cyan-400/10 border-cyan-400/30';
      case 'in_progress': return 'text-amber-400 bg-amber-400/10 border-amber-400/30';
      case 'resolved': return 'text-emerald-400 bg-emerald-400/10 border-emerald-400/30';
      case 'closed': return 'text-slate-400 bg-slate-400/10 border-slate-400/30';
      default: return 'text-slate-400 bg-slate-400/10 border-slate-400/30';
    }
  };

  const statusLabel = (status) => {
    switch (status) {
      case 'open': return 'Open';
      case 'in_progress': return 'In Progress';
      case 'resolved': return 'Resolved';
      case 'closed': return 'Closed';
      default: return status;
    }
  };

  // ---------- Render Helpers ----------

  const renderModeSelect = () => (
    <div className="space-y-8">
      <div className="text-center space-y-3">
        <h1 className="text-4xl sm:text-5xl font-bold text-white">
          Support <span className="text-cyan-400">Center</span>
        </h1>
        <p className="text-slate-400 text-base max-w-xl mx-auto">
          Need help with your device? Submit a repair request or check on an existing ticket.
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-3xl mx-auto">
        <button
          data-testid="submit-request-btn"
          onClick={() => setMode('submit')}
          className="group bg-slate-900/60 border border-slate-800 hover:border-cyan-500/50 rounded-2xl p-8 text-left transition-all duration-300 hover:shadow-lg hover:shadow-cyan-500/5"
        >
          <div className="w-14 h-14 bg-cyan-500/10 rounded-xl flex items-center justify-center mb-5 group-hover:bg-cyan-500/20 transition-colors">
            <Ticket className="w-7 h-7 text-cyan-400" />
          </div>
          <h3 className="text-xl font-semibold text-white mb-2">Submit a Request</h3>
          <p className="text-slate-400 text-sm">Tell us what's wrong with your device and we'll get to work.</p>
        </button>
        <button
          data-testid="update-ticket-btn"
          onClick={() => setMode('update')}
          className="group bg-slate-900/60 border border-slate-800 hover:border-cyan-500/50 rounded-2xl p-8 text-left transition-all duration-300 hover:shadow-lg hover:shadow-cyan-500/5"
        >
          <div className="w-14 h-14 bg-cyan-500/10 rounded-xl flex items-center justify-center mb-5 group-hover:bg-cyan-500/20 transition-colors">
            <MessageSquarePlus className="w-7 h-7 text-cyan-400" />
          </div>
          <h3 className="text-xl font-semibold text-white mb-2">Update a Ticket</h3>
          <p className="text-slate-400 text-sm">Add more info or updates to an existing repair ticket.</p>
        </button>
        <button
          data-testid="check-status-btn"
          onClick={() => setMode('check')}
          className="group bg-slate-900/60 border border-slate-800 hover:border-cyan-500/50 rounded-2xl p-8 text-left transition-all duration-300 hover:shadow-lg hover:shadow-cyan-500/5"
        >
          <div className="w-14 h-14 bg-cyan-500/10 rounded-xl flex items-center justify-center mb-5 group-hover:bg-cyan-500/20 transition-colors">
            <Search className="w-7 h-7 text-cyan-400" />
          </div>
          <h3 className="text-xl font-semibold text-white mb-2">Check Ticket Status</h3>
          <p className="text-slate-400 text-sm">Already submitted? Look up your ticket to see the latest updates.</p>
        </button>
      </div>
    </div>
  );

  const renderSubmitFlow = () => (
    <div className="max-w-lg mx-auto">
      <button data-testid="back-btn" onClick={resetAll} className="flex items-center gap-2 text-slate-400 hover:text-cyan-400 transition-colors mb-8">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>
      <h2 className="text-2xl font-bold text-white mb-1">Submit a Repair Request</h2>
      <p className="text-slate-400 text-sm mb-8">We'll verify your phone number to create your ticket.</p>

      {/* Progress */}
      <div className="flex items-center gap-2 mb-8">
        {[1, 2, 3].map(s => (
          <div key={s} className={`h-1.5 flex-1 rounded-full transition-colors ${step >= s ? 'bg-cyan-500' : 'bg-slate-800'}`} />
        ))}
      </div>

      {error && (
        <div data-testid="error-message" className="flex items-center gap-2 text-red-400 bg-red-400/10 border border-red-400/20 rounded-lg p-3 mb-6 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
        </div>
      )}

      {step === 1 && (
        <div className="space-y-5" data-testid="submit-step-1">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Your Name</label>
            <Input
              data-testid="customer-name-input"
              value={customerName}
              onChange={e => setCustomerName(e.target.value)}
              placeholder="John Doe"
              className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Device Type</label>
            <div className="grid grid-cols-5 gap-2">
              {DEVICE_TYPES.map(d => {
                const Icon = d.icon;
                return (
                  <button
                    key={d.value}
                    data-testid={`device-type-${d.value.toLowerCase()}`}
                    onClick={() => setDeviceType(d.value)}
                    className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border transition-all text-sm ${
                      deviceType === d.value
                        ? 'border-cyan-500 bg-cyan-500/10 text-cyan-400'
                        : 'border-slate-700 bg-slate-900 text-slate-400 hover:border-slate-600'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="text-xs">{d.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Describe the Issue</label>
            <textarea
              data-testid="issue-description-input"
              value={issueDescription}
              onChange={e => setIssueDescription(e.target.value)}
              rows={4}
              placeholder="My computer won't turn on, it makes a clicking noise..."
              className="w-full bg-slate-900 border border-slate-700 text-white placeholder:text-slate-500 rounded-lg p-3 focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500 resize-none text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Phone Number</label>
            <Input
              data-testid="phone-number-input"
              value={phoneNumber}
              onChange={e => setPhoneNumber(e.target.value)}
              placeholder="(218) 555-1234"
              type="tel"
              className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500"
            />
            <p className="text-xs text-slate-500 mt-1">We'll send a verification code to this number.</p>
          </div>
          <Button
            data-testid="send-code-btn"
            onClick={handleSendOTP}
            disabled={!customerName || !deviceType || !issueDescription || !phoneNumber || loading}
            className="w-full bg-cyan-500 hover:bg-cyan-600 text-white h-12 text-base disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (
              <span className="flex items-center gap-2"><Phone className="w-4 h-4" /> Send Verification Code</span>
            )}
          </Button>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-5" data-testid="submit-step-otp">
          <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <Shield className="w-5 h-5 text-cyan-400" />
              <div>
                <p className="text-white text-sm font-medium">Verification code sent</p>
                <p className="text-slate-400 text-xs">Check your phone for the 6-digit code</p>
              </div>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Enter Code</label>
            <Input
              data-testid="otp-code-input"
              value={otpCode}
              onChange={e => setOtpCode(e.target.value)}
              placeholder="123456"
              maxLength={6}
              className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500 text-center text-2xl tracking-[0.5em] font-mono"
            />
          </div>
          <Button
            data-testid="verify-submit-btn"
            onClick={handleVerifyAndSubmit}
            disabled={otpCode.length < 4 || loading}
            className="w-full bg-cyan-500 hover:bg-cyan-600 text-white h-12 text-base disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Verify & Submit Request'}
          </Button>
          <button
            data-testid="resend-code-btn"
            onClick={() => { setOtpCode(''); setError(''); handleSendOTP(); }}
            className="w-full text-sm text-slate-400 hover:text-cyan-400 transition-colors"
          >
            Didn't receive it? Resend code
          </button>
        </div>
      )}

      {step === 4 && createdTicket && (
        <div className="text-center space-y-6" data-testid="ticket-created-success">
          <div className="w-16 h-16 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto">
            <CheckCircle2 className="w-8 h-8 text-emerald-400" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-white mb-1">Request Submitted!</h3>
            <p className="text-slate-400 text-sm">Your ticket has been created. Save your ticket number below.</p>
          </div>
          <div className="bg-slate-900 border border-cyan-500/30 rounded-2xl p-6">
            <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Ticket Number</p>
            <p data-testid="ticket-id-display" className="text-3xl font-bold text-cyan-400 font-mono tracking-wider">{createdTicket.ticket_id}</p>
          </div>
          <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4 text-left space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-400">Name</span>
              <span className="text-white">{createdTicket.customer_name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">Device</span>
              <span className="text-white">{createdTicket.device_type}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">Status</span>
              <span className={`px-2 py-0.5 rounded-full border text-xs font-medium ${statusColor('open')}`}>Open</span>
            </div>
          </div>
          <Button data-testid="done-btn" onClick={resetAll} variant="outline" className="border-slate-700 text-slate-300 hover:text-white hover:border-slate-500">
            Done
          </Button>
        </div>
      )}
    </div>
  );

  const renderCheckFlow = () => (
    <div className="max-w-lg mx-auto">
      <button data-testid="back-btn-check" onClick={resetAll} className="flex items-center gap-2 text-slate-400 hover:text-cyan-400 transition-colors mb-8">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>
      <h2 className="text-2xl font-bold text-white mb-1">Check Ticket Status</h2>
      <p className="text-slate-400 text-sm mb-8">Verify your phone number to view your tickets.</p>

      {error && (
        <div data-testid="error-message-check" className="flex items-center gap-2 text-red-400 bg-red-400/10 border border-red-400/20 rounded-lg p-3 mb-6 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
        </div>
      )}

      {step === 1 && (
        <div className="space-y-5" data-testid="check-step-1">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Phone Number</label>
            <Input
              data-testid="check-phone-input"
              value={phoneNumber}
              onChange={e => setPhoneNumber(e.target.value)}
              placeholder="(218) 555-1234"
              type="tel"
              className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500"
            />
          </div>
          <Button
            data-testid="check-send-code-btn"
            onClick={handleSendOTP}
            disabled={!phoneNumber || loading}
            className="w-full bg-cyan-500 hover:bg-cyan-600 text-white h-12 text-base disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (
              <span className="flex items-center gap-2"><Phone className="w-4 h-4" /> Send Verification Code</span>
            )}
          </Button>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-5" data-testid="check-step-otp">
          <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <Shield className="w-5 h-5 text-cyan-400" />
              <div>
                <p className="text-white text-sm font-medium">Verification code sent</p>
                <p className="text-slate-400 text-xs">Check your phone for the 6-digit code</p>
              </div>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Enter Code</label>
            <Input
              data-testid="check-otp-input"
              value={otpCode}
              onChange={e => setOtpCode(e.target.value)}
              placeholder="123456"
              maxLength={6}
              className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500 text-center text-2xl tracking-[0.5em] font-mono"
            />
          </div>
          <Button
            data-testid="check-verify-btn"
            onClick={handleVerifyAndLookup}
            disabled={otpCode.length < 4 || loading}
            className="w-full bg-cyan-500 hover:bg-cyan-600 text-white h-12 text-base disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Verify & View Tickets'}
          </Button>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-6" data-testid="check-step-results">
          <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4">
            <p className="text-emerald-400 text-sm font-medium flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" /> Phone verified
            </p>
          </div>

          {allTickets.length > 0 ? (
            <div className="space-y-3">
              <p className="text-slate-300 text-sm font-medium">Your Tickets ({allTickets.length})</p>
              {allTickets.map(t => (
                <div
                  key={t.ticket_id}
                  data-testid={`ticket-card-${t.ticket_id}`}
                  className="bg-slate-900 border border-slate-800 rounded-xl p-4 space-y-2 hover:border-slate-700 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-cyan-400 font-mono font-semibold text-sm">{t.ticket_id}</span>
                    <span className={`px-2 py-0.5 rounded-full border text-xs font-medium ${statusColor(t.status)}`}>
                      {statusLabel(t.status)}
                    </span>
                  </div>
                  <p className="text-white text-sm">{t.device_type} — {t.issue_description.length > 80 ? t.issue_description.slice(0, 80) + '...' : t.issue_description}</p>
                  <p className="text-slate-500 text-xs">{new Date(t.created_at).toLocaleDateString()}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-slate-400">No tickets found for this phone number.</p>
            </div>
          )}

          <div className="border-t border-slate-800 pt-6">
            <p className="text-slate-300 text-sm font-medium mb-3">Look up a specific ticket</p>
            <div className="flex gap-2">
              <Input
                data-testid="ticket-lookup-input"
                value={ticketIdLookup}
                onChange={e => setTicketIdLookup(e.target.value)}
                placeholder="FB-XXXXXX"
                className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500 font-mono"
              />
              <Button
                data-testid="ticket-lookup-btn"
                onClick={handleLookupTicket}
                disabled={loading}
                className="bg-cyan-500 hover:bg-cyan-600 text-white px-6"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Look Up'}
              </Button>
            </div>
          </div>

          {lookupResult && (
            <div data-testid="ticket-lookup-result" className="bg-slate-900 border border-cyan-500/20 rounded-xl p-5 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-cyan-400 font-mono font-bold text-lg">{lookupResult.ticket_id}</span>
                <span className={`px-3 py-1 rounded-full border text-xs font-medium ${statusColor(lookupResult.status)}`}>
                  {statusLabel(lookupResult.status)}
                </span>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-slate-400">Name</span><span className="text-white">{lookupResult.customer_name}</span></div>
                <div className="flex justify-between"><span className="text-slate-400">Device</span><span className="text-white">{lookupResult.device_type}</span></div>
                {lookupResult.device_model && <div className="flex justify-between"><span className="text-slate-400">Model</span><span className="text-white">{lookupResult.device_model}</span></div>}
                {lookupResult.serial_number && <div className="flex justify-between"><span className="text-slate-400">Serial #</span><span className="text-white font-mono text-xs">{lookupResult.serial_number}</span></div>}
                <div className="flex justify-between"><span className="text-slate-400">Created</span><span className="text-white">{new Date(lookupResult.created_at).toLocaleString()}</span></div>
                <div className="flex justify-between"><span className="text-slate-400">Updated</span><span className="text-white">{new Date(lookupResult.updated_at).toLocaleString()}</span></div>
              </div>
              <div>
                <p className="text-slate-400 text-xs mb-1">Issue Description</p>
                <p className="text-white text-sm bg-slate-800/50 rounded-lg p-3">{lookupResult.issue_description}</p>
              </div>
              {lookupResult.customer_updates && lookupResult.customer_updates.length > 0 && (
                <div>
                  <p className="text-slate-400 text-xs mb-1">Your Updates</p>
                  <div className="space-y-2">
                    {lookupResult.customer_updates.map((u, i) => (
                      <div key={i} className="text-sm bg-blue-400/5 border border-blue-400/10 rounded-lg p-3">
                        <p className="text-blue-300">{u.message}</p>
                        <p className="text-slate-500 text-xs mt-1">{new Date(u.timestamp).toLocaleString()}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {lookupResult.admin_notes && (
                <div>
                  <p className="text-slate-400 text-xs mb-1">Tech Notes</p>
                  <p className="text-emerald-300 text-sm bg-emerald-400/5 border border-emerald-400/10 rounded-lg p-3">{lookupResult.admin_notes}</p>
                </div>
              )}
            </div>
          )}

          <Button data-testid="check-done-btn" onClick={resetAll} variant="outline" className="w-full border-slate-700 text-slate-300 hover:text-white hover:border-slate-500">
            Done
          </Button>
        </div>
      )}
    </div>
  );

  const renderUpdateFlow = () => (
    <div className="max-w-lg mx-auto">
      <button data-testid="back-btn-update" onClick={resetAll} className="flex items-center gap-2 text-slate-400 hover:text-cyan-400 transition-colors mb-8">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>
      <h2 className="text-2xl font-bold text-white mb-1">Update a Ticket</h2>
      <p className="text-slate-400 text-sm mb-8">Verify your phone number to add updates to your ticket.</p>

      {error && (
        <div data-testid="error-message-update" className="flex items-center gap-2 text-red-400 bg-red-400/10 border border-red-400/20 rounded-lg p-3 mb-6 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
        </div>
      )}

      {step === 1 && (
        <div className="space-y-5" data-testid="update-step-1">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Phone Number</label>
            <Input
              data-testid="update-phone-input"
              value={phoneNumber}
              onChange={e => setPhoneNumber(e.target.value)}
              placeholder="(218) 555-1234"
              type="tel"
              className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500"
            />
          </div>
          <Button
            data-testid="update-send-code-btn"
            onClick={handleSendOTP}
            disabled={!phoneNumber || loading}
            className="w-full bg-cyan-500 hover:bg-cyan-600 text-white h-12 text-base disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (
              <span className="flex items-center gap-2"><Phone className="w-4 h-4" /> Send Verification Code</span>
            )}
          </Button>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-5" data-testid="update-step-otp">
          <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <Shield className="w-5 h-5 text-cyan-400" />
              <div>
                <p className="text-white text-sm font-medium">Verification code sent</p>
                <p className="text-slate-400 text-xs">Check your phone for the 6-digit code</p>
              </div>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Enter Code</label>
            <Input
              data-testid="update-otp-input"
              value={otpCode}
              onChange={e => setOtpCode(e.target.value)}
              placeholder="123456"
              maxLength={6}
              className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500 text-center text-2xl tracking-[0.5em] font-mono"
            />
          </div>
          <Button
            data-testid="update-verify-btn"
            onClick={handleVerifyAndFetchForUpdate}
            disabled={otpCode.length < 4 || loading}
            className="w-full bg-cyan-500 hover:bg-cyan-600 text-white h-12 text-base disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Verify & View Tickets'}
          </Button>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-6" data-testid="update-step-select">
          <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4">
            <p className="text-emerald-400 text-sm font-medium flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" /> Phone verified
            </p>
          </div>

          {allTickets.length > 0 ? (
            <>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Select a ticket to update</label>
                <div className="space-y-2">
                  {allTickets.map(t => (
                    <button
                      key={t.ticket_id}
                      data-testid={`update-select-${t.ticket_id}`}
                      onClick={() => setUpdateTicketId(t.ticket_id)}
                      className={`w-full text-left p-3 rounded-xl border transition-all ${
                        updateTicketId === t.ticket_id
                          ? 'border-cyan-500 bg-cyan-500/10'
                          : 'border-slate-800 bg-slate-900 hover:border-slate-700'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-cyan-400 font-mono text-sm font-semibold">{t.ticket_id}</span>
                        <span className={`px-2 py-0.5 rounded-full border text-xs font-medium ${statusColor(t.status)}`}>
                          {statusLabel(t.status)}
                        </span>
                      </div>
                      <p className="text-slate-300 text-sm mt-1">{t.device_type} — {t.issue_description.length > 60 ? t.issue_description.slice(0, 60) + '...' : t.issue_description}</p>
                    </button>
                  ))}
                </div>
              </div>
              {updateTicketId && (
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Your Update</label>
                  <textarea
                    data-testid="update-message-input"
                    value={updateMessage}
                    onChange={e => setUpdateMessage(e.target.value)}
                    rows={4}
                    placeholder="I also noticed the keyboard stopped working..."
                    className="w-full bg-slate-900 border border-slate-700 text-white placeholder:text-slate-500 rounded-lg p-3 focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500 resize-none text-sm"
                  />
                  <Button
                    data-testid="submit-update-btn"
                    onClick={handleSubmitUpdate}
                    disabled={!updateMessage.trim() || loading}
                    className="w-full bg-cyan-500 hover:bg-cyan-600 text-white h-12 text-base disabled:opacity-50 mt-3"
                  >
                    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Submit Update'}
                  </Button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-8">
              <p className="text-slate-400">No tickets found for this phone number.</p>
            </div>
          )}
        </div>
      )}

      {step === 4 && updateSuccess && (
        <div className="text-center space-y-6" data-testid="update-success">
          <div className="w-16 h-16 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto">
            <CheckCircle2 className="w-8 h-8 text-emerald-400" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-white mb-1">Update Submitted!</h3>
            <p className="text-slate-400 text-sm">Your update has been added to ticket <span className="text-cyan-400 font-mono">{updateTicketId}</span>.</p>
          </div>
          <Button data-testid="update-done-btn" onClick={resetAll} variant="outline" className="border-slate-700 text-slate-300 hover:text-white hover:border-slate-500">
            Done
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-950">
      <Header />
      <div className="pt-32 pb-20 px-6">
        {mode === null && renderModeSelect()}
        {mode === 'submit' && renderSubmitFlow()}
        {mode === 'update' && renderUpdateFlow()}
        {mode === 'check' && renderCheckFlow()}
      </div>
      <Footer />
    </div>
  );
};

export default SupportPage;
